import React, { useMemo, useState, useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from './chartInit';
import { getThemeConfig, NON_STOCK_TYPES } from './chartTheme';
import { ChartErrorBoundary, ChartErrorIcon, TickerInfoHeader } from './chartComponents';
import { useContainerWidth } from './hooks/useContainerWidth';

function isGaugeChart(chartSpec) {
    if (!chartSpec) return false;
    const type = chartSpec.chart?.type;
    if (type === 'gauge' || type === 'solidgauge') return true;
    return chartSpec.series?.some(s => s.type === 'gauge' || s.type === 'solidgauge') || false;
}

function isMultiPaneGauge(chartSpec) {
    return isGaugeChart(chartSpec) && Array.isArray(chartSpec.pane) && chartSpec.pane.length > 1;
}

function splitGaugeSpecs(chartSpec) {
    if (!isMultiPaneGauge(chartSpec)) return null;

    const panes = chartSpec.pane;
    const yAxes = Array.isArray(chartSpec.yAxis) ? chartSpec.yAxis : [chartSpec.yAxis].filter(Boolean);
    const allSeries = Array.isArray(chartSpec.series) ? chartSpec.series : [chartSpec.series].filter(Boolean);

    const sharedProps = {};
    const skipKeys = new Set(['pane', 'yAxis', 'xAxis', 'series', 'title']);
    Object.keys(chartSpec).forEach(key => {
        if (!skipKeys.has(key)) {
            sharedProps[key] = chartSpec[key];
        }
    });

    return panes.map((pane, paneIndex) => {
        const paneYAxes = yAxes
            .map((axis, idx) => ({ axis, originalIndex: idx }))
            .filter(({ axis }) => axis.pane === paneIndex);

        const originalYAxisIndices = new Set(paneYAxes.map(({ originalIndex }) => originalIndex));
        const paneSeries = allSeries.filter(s => {
            const seriesYAxis = s.yAxis ?? 0;
            return originalYAxisIndices.has(seriesYAxis);
        });

        const yAxisIndexMap = new Map();
        paneYAxes.forEach(({ originalIndex }, newIndex) => {
            yAxisIndexMap.set(originalIndex, newIndex);
        });

        const remappedYAxes = paneYAxes.map(({ axis }) => {
            const { pane: _pane, ...rest } = axis;
            return rest;
        });

        const remappedSeries = paneSeries.map(s => {
            const newYAxis = yAxisIndexMap.get(s.yAxis ?? 0) ?? 0;
            return { ...s, yAxis: newYAxis };
        });

        const title = remappedSeries[0]?.name
            || remappedYAxes[0]?.title?.text
            || null;

        return {
            ...sharedProps,
            title: title ? { text: title } : { text: null },
            pane: { ...pane, center: ['50%', '50%'] },
            yAxis: remappedYAxes.length === 1 ? remappedYAxes[0] : remappedYAxes,
            series: remappedSeries,
        };
    });
}

function applyGaugeTheme(chartSpec, isDarkMode) {
    const minimalTheme = {
        chart: {
            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
            style: { fontFamily: 'Inter, system-ui, sans-serif' },
        },
        title: {
            style: {
                color: isDarkMode ? '#e5e5e5' : '#333333',
                fontSize: '14px',
                fontWeight: '600',
            },
        },
        credits: { enabled: false },
        exporting: { enabled: false },
    };
    return Highcharts.merge(minimalTheme, chartSpec);
}

function applyTheme(chartSpec, isDarkMode) {
    if (isGaugeChart(chartSpec)) {
        return applyGaugeTheme(chartSpec, isDarkMode);
    }

    const theme = getThemeConfig(isDarkMode);
    const merged = Highcharts.merge(theme, chartSpec);

    if (merged.series && Array.isArray(merged.series)) {
        merged.series.forEach(s => {
            if (s.type === 'treemap' && Array.isArray(s.data)) {
                const parentIds = new Set(
                    s.data.filter(p => p.parent).map(p => p.parent)
                );
                s.data.forEach(point => {
                    if (parentIds.has(point.id) && point.value === 0) {
                        delete point.value;
                    }
                });
            }
        });
    }

    let extractedCategories = null;
    if (merged.series && Array.isArray(merged.series)) {
        const firstSeries = merged.series[0];
        if (firstSeries?.data && Array.isArray(firstSeries.data)) {
            const hasNamedPoints = firstSeries.data.every(
                point => point && typeof point === 'object' && !Array.isArray(point) && point.name !== undefined
            );
            if (hasNamedPoints) {
                extractedCategories = firstSeries.data.map(point => point.name);
            }
        }
    }

    if (merged.xAxis) {
        const axes = Array.isArray(merged.xAxis) ? merged.xAxis : [merged.xAxis];
        axes.forEach((axis) => {
            const categories = (axis.categories && Array.isArray(axis.categories) && axis.categories.length > 0)
                ? axis.categories
                : extractedCategories;

            if (categories && categories.length > 0) {
                axis.categories = categories;
                axis.type = 'category';
                delete axis.dateTimeLabelFormats;
                if (axis.crosshair?.label?.format) {
                    delete axis.crosshair.label.format;
                }
            }
        });
        merged.xAxis = Array.isArray(chartSpec.xAxis) ? axes : axes[0];
    } else if (extractedCategories) {
        merged.xAxis = {
            type: 'category',
            categories: extractedCategories,
        };
    }

    if (!merged.chart) {
        merged.chart = {};
    }

    const isTreemap = merged.chart?.type === 'treemap'
        || merged.series?.some(s => s.type === 'treemap');
    const isPolarChart = merged.chart?.polar === true;

    if (merged.yAxis) {
        const axes = Array.isArray(merged.yAxis) ? merged.yAxis : [merged.yAxis];
        const numPanels = axes.length;
        const requiredHeight = 300 + (numPanels - 1) * 50;
        if (!merged.chart.height || merged.chart.height < requiredHeight) {
            merged.chart.height = requiredHeight;
        }
    }

    merged.chart.marginLeft = null;
    merged.chart.marginRight = null;
    merged.chart.spacing = [10, 10, 15, 10];

    if (merged.yAxis) {
        const isArray = Array.isArray(merged.yAxis);
        const axes = isArray ? merged.yAxis.filter(axis => axis != null) : [merged.yAxis];
        const lineColor = isDarkMode ? '#282828' : '#e5e7eb';
        const gridColor = isDarkMode ? '#374151' : '#e5e7eb';

        axes.forEach((axis) => {
            axis.labels = axis.labels || {};
            axis.labels.style = {
                ...(axis.labels.style || {}),
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                fontSize: '11px',
            };

            if (isPolarChart) {
                axis.gridLineWidth = axis.gridLineWidth || 1;
                axis.gridLineColor = axis.gridLineColor || gridColor;
            } else {
                axis.labels.align = undefined;
                axis.labels.x = undefined;
                axis.gridLineWidth = 0;
                axis.lineColor = lineColor;
                axis.lineWidth = 0;
                axis.offset = 0;
            }

            if (axes.length === 1 && !isPolarChart) {
                axis.title = { text: null };
            }
        });

        merged.yAxis = isArray ? axes : axes[0];
    }

    if (isPolarChart && merged.xAxis) {
        const xAxes = Array.isArray(merged.xAxis) ? merged.xAxis : [merged.xAxis];
        const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
        xAxes.forEach((axis) => {
            axis.gridLineWidth = axis.gridLineWidth || 1;
            axis.gridLineColor = axis.gridLineColor || gridColor;
        });
        merged.xAxis = Array.isArray(chartSpec.xAxis) ? xAxes : xAxes[0];
    }

    if (isTreemap) {
        const borderColor = isDarkMode ? '#1f2937' : '#ffffff';

        merged.chart.spacing = [2, 2, 2, 2];
        merged.chart.marginLeft = 0;
        merged.chart.marginRight = 0;

        merged.xAxis = { visible: false, crosshair: false };
        merged.yAxis = { visible: false, crosshair: false };

        merged.tooltip = {
            ...merged.tooltip,
            outside: true,
            useHTML: true,
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
            borderWidth: 1,
            borderColor: isDarkMode ? '#374151' : '#e5e7eb',
            borderRadius: 8,
            shadow: false,
            padding: 10,
            shared: false,
            split: false,
            style: {
                color: isDarkMode ? '#e5e5e5' : '#333333',
                fontSize: '13px',
            },
        };

        merged.plotOptions = merged.plotOptions || {};
        merged.plotOptions.series = {
            ...(merged.plotOptions.series || {}),
            lineWidth: 0,
            marker: { enabled: false },
        };
        merged.plotOptions.treemap = Highcharts.merge(
            merged.plotOptions.treemap || {},
            {
                borderWidth: 4,
                borderColor,
                borderRadius: 8,
                dataLabels: {
                    enabled: true,
                    style: {
                        textOutline: 'none',
                        color: isDarkMode ? '#e5e5e5' : '#374151',
                        fontSize: '12px',
                        fontWeight: '600',
                    },
                },
                states: {
                    hover: {
                        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                        brightness: 0.05,
                    },
                },
            }
        );

        merged.series?.forEach(s => {
            if (s.type !== 'treemap' || !Array.isArray(s.levels)) return;
            s.levels.forEach(lvl => {
                if (lvl.level === 1 && lvl.dataLabels) {
                    lvl.dataLabels = Highcharts.merge(lvl.dataLabels, {
                        headers: true,
                        align: 'left',
                        verticalAlign: 'top',
                        style: {
                            ...lvl.dataLabels.style,
                            textOutline: 'none',
                            textTransform: 'uppercase',
                            color: isDarkMode ? '#e5e5e5' : '#1f2937',
                        },
                    });
                    lvl.groupPadding = lvl.groupPadding ?? 3;
                    lvl.borderWidth = lvl.borderWidth ?? 1;
                    lvl.borderRadius = lvl.borderRadius ?? 4;
                }
            });
        });
    }

    return merged;
}

export default function ChartWidget({ data, config }) {
    const isDarkMode = document.documentElement.classList.contains('dark');
    const { containerRef, containerWidth, isMobile, isCompact } = useContainerWidth(500);

    const chartSpec = useMemo(() => {
        if (!data && !config) return null;
        if (data?.data && (data.artifact_id || data.widget_type)) {
            return data.data;
        }
        return data || config?.data;
    }, [data, config]);

    const [tickerInfoData, setTickerInfoData] = useState([]);
    const [tickerInfoLoading, setTickerInfoLoading] = useState(false);

    const tickers = useMemo(() => {
        const symbols = chartSpec?.metadata?.symbols || config?.metadata?.symbols || [];
        return Array.isArray(symbols) ? symbols : (symbols ? [symbols] : []);
    }, [chartSpec, config]);

    useEffect(() => {
        if (tickers.length === 0) {
            setTickerInfoData([]);
            return;
        }

        const fetchTickerInfo = async () => {
            setTickerInfoLoading(true);
            try {
                const apiBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'https://api.simplize.dev';
                const promises = tickers.map(ticker =>
                    fetch(`${apiBaseUrl}/api/company/summary/${ticker}`)
                        .then(res => {
                            if (!res.ok) throw new Error(`Failed to fetch ${ticker}`);
                            return res.json();
                        })
                        .then(response => {
                            const actualData = response.data || response;
                            return { ticker, data: actualData, error: null };
                        })
                        .catch(err => ({ ticker, data: null, error: err.message }))
                );
                const results = await Promise.all(promises);
                setTickerInfoData(results);
            } catch (err) {
                console.error('Error fetching ticker info:', err);
            } finally {
                setTickerInfoLoading(false);
            }
        };

        fetchTickerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickers.join(',')]);

    const chartType = useMemo(() => {
        if (!chartSpec) return null;
        const specType = chartSpec.chart?.type;
        if (specType) return specType;
        const series = chartSpec.series;
        if (Array.isArray(series) && series.length > 0) {
            return series[0].type || null;
        }
        return null;
    }, [chartSpec]);

    const isCompactChart = useMemo(() => {
        const compactTypes = ['gauge', 'solidgauge', 'pie', 'variablepie', 'sunburst', 'venn'];
        return compactTypes.includes(chartType);
    }, [chartType]);

    const isTallChart = useMemo(() => {
        const tallTypes = ['column', 'bar', 'waterfall', 'histogram', 'bullet', 'lollipop', 'dumbbell'];
        return tallTypes.includes(chartType);
    }, [chartType]);

    const responsiveHeight = useMemo(() => {
        if (!chartSpec) return 300;

        const yAxes = chartSpec.yAxis;
        const numPanels = Array.isArray(yAxes) ? yAxes.length : (yAxes ? 1 : 1);
        const serverHeight = chartSpec.chart?.height || 300;

        if (isCompactChart) {
            if (isCompact) return Math.max(150, Math.min(200, Math.round(containerWidth * 0.5)));
            if (isMobile) return Math.max(180, Math.min(250, Math.round(containerWidth * 0.55)));
            return Math.min(300, Math.max(200, serverHeight));
        }

        if (isTallChart) {
            if (isCompact) return Math.max(250, Math.min(350, Math.round(containerWidth * 0.8)));
            if (isMobile) return Math.max(300, Math.min(420, Math.round(containerWidth * 0.85)));
            return Math.min(500, Math.max(350, serverHeight));
        }

        if (isCompact) {
            const baseHeight = Math.round(containerWidth * 0.6);
            const panelBonus = (numPanels - 1) * 30;
            return Math.max(180, Math.min(280 + panelBonus, baseHeight + panelBonus));
        }

        if (isMobile) {
            const baseHeight = Math.round(containerWidth * 0.7);
            const panelBonus = (numPanels - 1) * 40;
            return Math.max(220, Math.min(350 + panelBonus, baseHeight + panelBonus));
        }

        const panelBonus = (numPanels - 1) * 50;
        return Math.min(500, Math.max(300, serverHeight) + panelBonus);
    }, [chartSpec, containerWidth, isMobile, isCompact, isCompactChart, isTallChart]);

    const chartOptions = useMemo(() => {
        if (!chartSpec) return null;
        const options = applyTheme(chartSpec, isDarkMode);
        if (options.chart && !isCompactChart) {
            options.chart.height = responsiveHeight;
        }
        return options;
    }, [chartSpec, isDarkMode, responsiveHeight, containerWidth, isCompactChart]);

    const constructorType = useMemo(() => {
        if (!chartSpec) return 'stockChart';

        const xAxis = chartSpec.xAxis;
        if (xAxis) {
            const axes = Array.isArray(xAxis) ? xAxis : [xAxis];
            for (const axis of axes) {
                if (axis.categories && Array.isArray(axis.categories) && axis.categories.length > 0) {
                    return 'chart';
                }
                if (axis.type === 'category') {
                    return 'chart';
                }
            }
        }

        const chartType = chartSpec.chart?.type;
        if (chartType && NON_STOCK_TYPES.includes(chartType)) {
            return 'chart';
        }

        return 'stockChart';
    }, [chartSpec]);

    const gaugeSpecs = useMemo(() => {
        if (!chartSpec || !isMultiPaneGauge(chartSpec)) return null;
        const specs = splitGaugeSpecs(chartSpec);
        if (!specs) return null;
        return specs.map(spec => applyGaugeTheme(spec, isDarkMode));
    }, [chartSpec, isDarkMode]);

    if (!chartSpec) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #f3f4f6',
            }}>
                <ChartErrorIcon isDarkMode={isDarkMode} />
                <p style={{
                    marginTop: '16px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    fontSize: '14px',
                }}>
                    Widget không khả dụng
                </p>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            style={{ marginTop: '12px', marginBottom: '12px' }}
        >
            <div style={{
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #f3f4f6',
                overflow: 'hidden',
            }}>
                <TickerInfoHeader
                    tickers={tickers}
                    tickerInfoData={tickerInfoData}
                    tickerInfoLoading={tickerInfoLoading}
                    isDarkMode={isDarkMode}
                />

                <ChartErrorBoundary>
                    <style>{`
                        .chart-widget-wrapper .highcharts-exporting-group {
                            opacity: 0;
                            transition: opacity 0.2s ease-in-out;
                        }
                        .chart-widget-wrapper:hover .highcharts-exporting-group {
                            opacity: 1;
                        }
                    `}</style>
                    {gaugeSpecs ? (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            padding: '8px 12px',
                            gap: '4px',
                        }}>
                            {gaugeSpecs.map((spec, index) => (
                                <div key={index} style={{
                                    flex: '1 1 200px',
                                    maxWidth: '400px',
                                    minWidth: '180px',
                                }}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        constructorType="chart"
                                        options={spec}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="chart-widget-wrapper" style={{
                            backgroundColor: 'transparent',
                            padding: '8px 12px',
                        }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                constructorType={constructorType}
                                options={chartOptions}
                            />
                        </div>
                    )}
                </ChartErrorBoundary>
            </div>
        </div>
    );
}
